import localforage from "localforage";

import type { VehicleCustomLayer } from "../components/MapLibre/VehicleCustomLayer";
import { APP_DIAGNOSTICS } from "../config/global.configuration";
import type { InterpolationEngine } from "../engine/InterpolationEngine";
import type { Color } from "../model/AtlasConfig";
import type { RealtimeCache } from "../model/RealtimeCache";
import type { Vehicle } from "../model/Vehicle";
import type { VehicleType } from "../model/VehicleType";
import { tripRecordingService } from "../services/navigation/TripRecordingService";
import { tripReplayService } from "../services/navigation/TripReplayService";
import type { DrawnItineraryGeometry } from "../store/ephemeral";
import { ALL_EPHEMERAL_SLICES, drawnItineraryGeometrySlice, ephemeralStore } from "../store/ephemeral";
import { persistentSliceStore } from "../store/persistence/runtime/PersistentSliceStore";
import { ALL_PERSISTED_SLICES } from "../store/persistence/slices/registry";
import type {
    AppMemoryCategoryTotals,
    AppMemoryReport,
    IndexedDbCacheMemoryMetrics,
    ItineraryMapMemoryStats,
    MapStopsMemoryStats,
    MemoryScanDepth,
    SliceMemoryMetric,
    TripBuffersMemoryDiagnostics,
    VehicleLayerMemoryDiagnostics,
} from "./AppMemoryReport.types";
import { measureMapLibreMemoryHints } from "./MapLibreMemoryHints.tools";
import { MEMORY_DIAGNOSTICS_LOG_PREFIX, memoryDiagnosticsWarn } from "./MemoryDiagnosticsLog.tools";
import {
    estimateJsonUtf8Bytes,
    readJsHeapMetrics,
} from "./MemoryEstimate.tools";
import {
    computeHeapUsedDelta,
    logFormattedMemoryReport,
    resolveMemoryLogDepth,
    type MemoryLogDepth,
} from "./MemoryLogFormatter.tools";
import {
    buildRealtimeMemoryReport,
    type RealtimeMemoryReport,
} from "./RealtimeMemoryProfiler.tools";

export type {
    AppMemoryCategoryTotals,
    AppMemoryReport,
    IndexedDbCacheMemoryMetrics,
    ItineraryMapMemoryStats,
    MapStopsMemoryStats,
    SliceMemoryMetric,
    TripBuffersMemoryDiagnostics,
    VehicleLayerMemoryDiagnostics,
} from "./AppMemoryReport.types";

/** Last map stops fetch stats (updated by {@link recordMapStopsMemoryStats}). */
let lastMapStopsStats: MapStopsMemoryStats | null = null;

/** Last itinerary map overlay stats (updated by {@link recordItineraryMapMemoryStats}). */
let lastItineraryMapStats: ItineraryMapMemoryStats | null = null;

/** Optional bound vehicle WebGL layer for diagnostics. */
let boundVehicleLayer: VehicleCustomLayer | null = null;

/** Bound realtime cache + vehicle manager used to fill global snapshots on every log. */
let realtimeBindings: {
    "cache":            RealtimeCache;
    "getVehicleInputs": () => {
        "vehicles":            Map<string, Vehicle>;
        "colorCache":          Map<string, Color>;
        "typeCache":           Map<string, VehicleType>;
        "interpolationEngine": InterpolationEngine;
    };
} | null = null;

/** Last active itinerary payload seen by the planner (for global totals when not passed explicitly). */
let lastActiveItineraryForProfiling: unknown = null;

/** Last measured axios-cache footprint (IndexedDB); refreshed on periodic / manual scans. */
let lastIndexedDbAxiosCache: IndexedDbCacheMemoryMetrics | null = null;

/** Previous full report used for delta tracking and light scans. */
let lastReport: AppMemoryReport | null = null;

/** Timestamp of the last full (stringify) scan. */
let lastFullScanTimestampMs = 0;

/** Cached slice metrics from the last full scan. */
let cachedFullScanSlices: {
    "ephemeralSlices":      Record<string, SliceMemoryMetric>;
    "persistedSlices":      Record<string, SliceMemoryMetric>;
    "drawnItinerary":       AppMemoryReport["drawnItinerary"];
    "activeItineraryBytes": number;
} | null = null;

/** Pending idle callback id for deferred full logs. */
let pendingIdleLogHandle: number | null = null;

let lastLogTimestampMs = 0;

/**
 * Records stats from the latest stops-in-view fetch.
 *
 * @param stats Parent/child feature counts and byte estimate.
 * @returns Nothing.
 */
export function recordMapStopsMemoryStats( stats: MapStopsMemoryStats ): void {
    lastMapStopsStats = stats;
}

/**
 * Records stats from the latest itinerary map draw pass.
 *
 * @param stats Leg layers and drawn geometry footprint.
 * @returns Nothing.
 */
export function recordItineraryMapMemoryStats( stats: ItineraryMapMemoryStats ): void {
    lastItineraryMapStats = stats;
}

/**
 * Binds the vehicle custom layer used for WebGL diagnostics.
 *
 * @param layer Active {@link VehicleCustomLayer}, or null on teardown.
 * @returns Nothing.
 */
export function bindVehicleLayerForMemoryProfiling( layer: VehicleCustomLayer | null ): void {
    boundVehicleLayer = layer;
}

/**
 * Binds realtime cache and vehicle inputs so every memory log includes the full realtime picture.
 *
 * @param bindings Active cache and vehicle profiling supplier, or null on teardown.
 * @returns Nothing.
 */
export function bindRealtimeMemoryProfiling( bindings: {
    "cache":            RealtimeCache;
    "getVehicleInputs": () => {
        "vehicles":            Map<string, Vehicle>;
        "colorCache":          Map<string, Color>;
        "typeCache":           Map<string, VehicleType>;
        "interpolationEngine": InterpolationEngine;
    };
} | null ): void {
    realtimeBindings = bindings;
}

/**
 * Remembers the latest planner itinerary for global memory totals.
 *
 * @param itinerary Active itinerary DTO, or null when cleared.
 * @returns Nothing.
 */
export function recordActiveItineraryForMemoryProfiling( itinerary: unknown ): void {
    lastActiveItineraryForProfiling = itinerary;
}

/**
 * Sums estimated bytes across slice metrics.
 *
 * @param slices Per-slice metrics map.
 * @returns Total bytes.
 */
function sumSliceMetricsBytes( slices: Record<string, SliceMemoryMetric> ): number {
    return Object.values( slices ).reduce( ( total, slice ) => total + slice.estimatedBytes, 0 );
}

/**
 * Merges explicit realtime params with bound global handles.
 *
 * @param explicit Optional realtime handles from the call site.
 * @returns Resolved realtime params, or undefined when unavailable.
 */
function resolveRealtimeParams( explicit?: {
    "cache":                RealtimeCache;
    "vehicles"?:            Map<string, Vehicle>;
    "colorCache"?:          Map<string, Color>;
    "typeCache"?:           Map<string, VehicleType>;
    "interpolationEngine"?: InterpolationEngine;
} ): {
    "cache":                RealtimeCache;
    "vehicles"?:            Map<string, Vehicle>;
    "colorCache"?:          Map<string, Color>;
    "typeCache"?:           Map<string, VehicleType>;
    "interpolationEngine"?: InterpolationEngine;
} | undefined {
    const bound = realtimeBindings;
    const cache = explicit?.cache ?? bound?.cache;
    if ( cache === undefined ) {
        return undefined;
    }

    const vehicleInputs = bound?.getVehicleInputs();

    return {
        cache,
        "vehicles":            explicit?.vehicles ?? vehicleInputs?.vehicles,
        "colorCache":          explicit?.colorCache ?? vehicleInputs?.colorCache,
        "typeCache":           explicit?.typeCache ?? vehicleInputs?.typeCache,
        "interpolationEngine": explicit?.interpolationEngine ?? vehicleInputs?.interpolationEngine,
    };
}

/**
 * Returns whether cached full-scan slice metrics are still fresh enough to reuse.
 *
 * @returns True when a recent full scan exists within {@link APP_DIAGNOSTICS.memoryFullScanMaxAgeMs}.
 */
function isFullScanCacheFresh(): boolean {
    return cachedFullScanSlices !== null &&
      Date.now() - lastFullScanTimestampMs < APP_DIAGNOSTICS.memoryFullScanMaxAgeMs;
}

/**
 * Resolves how deeply {@link buildAppMemoryReport} should measure subsystems.
 *
 * @param params Log parameters and throttle flags.
 * @param now Current timestamp in milliseconds.
 * @returns light, standard, or full scan depth.
 */
function resolveScanDepth(
    params: {
        "context":       string;
        "skipThrottle"?: boolean;
    },
    now: number,
): MemoryScanDepth {
    if ( params.skipThrottle === true || params.context === "manual" ) {
        return "full";
    }

    if ( now - lastLogTimestampMs < APP_DIAGNOSTICS.memoryProfilingMinIntervalMs ) {
        return "light";
    }

    if ( params.context === "periodic" ) {
        return "full";
    }

    return "standard";
}

/**
 * Builds a heap-only snapshot by reusing the last full report category totals.
 *
 * @param context Trigger label for the light snapshot.
 * @returns Updated report, or null when no previous report exists.
 */
function buildLightReport( context: string ): AppMemoryReport | null {
    if ( lastReport === null ) {
        return null;
    }

    const heap = readJsHeapMetrics();
    const trackedTotalBytes = lastReport.categoryTotals.trackedTotalBytes;
    const jsHeapUsedBytes = heap.usedJSHeapSize;
    const categoryTotals: AppMemoryCategoryTotals = {
        ...lastReport.categoryTotals,
        "mapStopsBytes": lastReport.categoryTotals.mapStopsBytes ??
          lastReport.mapStops?.estimatedBytes ??
          0,
        "unaccountedHeapBytes": jsHeapUsedBytes !== undefined
            ? Math.max( 0, jsHeapUsedBytes - trackedTotalBytes )
            : undefined,
        jsHeapUsedBytes,
        "jsHeapTotalBytes": heap.totalJSHeapSize,
    };

    return {
        ...lastReport,
        context,
        heap,
        categoryTotals,
        "estimatedTotalBytes": trackedTotalBytes,
    };
}

/**
 * Schedules a memory log on idle time, replacing any pending idle callback.
 *
 * @param callback Work to run when the browser is idle.
 * @returns Nothing.
 */
function scheduleDeferredMemoryLog( callback: () => void ): void {
    if ( pendingIdleLogHandle !== null ) {
        if ( typeof cancelIdleCallback !== "undefined" ) {
            cancelIdleCallback( pendingIdleLogHandle );
        } else {
            clearTimeout( pendingIdleLogHandle );
        }
    }

    if ( typeof requestIdleCallback !== "undefined" ) {
        pendingIdleLogHandle = requestIdleCallback( () => {
            pendingIdleLogHandle = null;
            callback();
        }, { "timeout": 5_000 } );

        return;
    }

    pendingIdleLogHandle = window.setTimeout( () => {
        pendingIdleLogHandle = null;
        callback();
    }, 0 );
}

/**
 * Builds per-category totals from report fields.
 *
 * @param parts Measured subsystem payloads.
 * @returns Category-level byte sums including tracked grand total.
 */
function buildCategoryTotalsFromParts( parts: {
    "ephemeralSlices":      Record<string, SliceMemoryMetric>;
    "persistedSlices":      Record<string, SliceMemoryMetric>;
    "drawnItinerary":       SliceMemoryMetric;
    "mapStops":             MapStopsMemoryStats | null;
    "itineraryMap":         ItineraryMapMemoryStats | null;
    "activeItineraryBytes": number;
    "mapLibreTrackedBytes": number;
    "realtime":             RealtimeMemoryReport | null;
    "vehicleLayer":         VehicleLayerMemoryDiagnostics | null;
    "tripBuffers":          TripBuffersMemoryDiagnostics;
    "indexedDbAxiosCache":  IndexedDbCacheMemoryMetrics | null;
    "heap":                 ReturnType<typeof readJsHeapMetrics>;
} ): AppMemoryCategoryTotals {
    const mapStopsBytes = parts.mapStops?.estimatedBytes ?? 0;

    const mapOverlayBytes = ( parts.itineraryMap?.estimatedMapLayerBytes ?? 0 ) +
      parts.drawnItinerary.estimatedBytes +
      parts.activeItineraryBytes;

    const vehicleLayerBytes = ( parts.vehicleLayer?.textureCacheEstimatedBytes ?? 0 ) +
      ( parts.vehicleLayer?.instanceCornerBytes ?? 0 );

    const tripBuffersBytes = parts.tripBuffers.estimatedBytes + parts.tripBuffers.replayEstimatedBytes;

    const indexedDbCacheBytes = parts.indexedDbAxiosCache?.estimatedBytes ?? 0;

    const realtimeBytes = parts.realtime?.estimatedTotalBytes ?? 0;

    const ephemeralStoreBytes = sumSliceMetricsBytes( parts.ephemeralSlices );
    const persistedStoreBytes = sumSliceMetricsBytes( parts.persistedSlices );
    const mapLibreTrackedBytes = parts.mapLibreTrackedBytes;

    const trackedTotalBytes = ephemeralStoreBytes +
      persistedStoreBytes +
      mapStopsBytes +
      mapOverlayBytes +
      mapLibreTrackedBytes +
      realtimeBytes +
      vehicleLayerBytes +
      tripBuffersBytes +
      indexedDbCacheBytes;

    const jsHeapUsedBytes = parts.heap.usedJSHeapSize;
    const unaccountedHeapBytes = jsHeapUsedBytes !== undefined
        ? Math.max( 0, jsHeapUsedBytes - trackedTotalBytes )
        : undefined;

    return {
        ephemeralStoreBytes,
        persistedStoreBytes,
        mapStopsBytes,
        mapOverlayBytes,
        mapLibreTrackedBytes,
        realtimeBytes,
        vehicleLayerBytes,
        tripBuffersBytes,
        indexedDbCacheBytes,
        trackedTotalBytes,
        unaccountedHeapBytes,
        "jsHeapUsedBytes":  jsHeapUsedBytes,
        "jsHeapTotalBytes": parts.heap.totalJSHeapSize,
    };
}

/**
 * Builds per-category totals from a full {@link AppMemoryReport}.
 *
 * @param report Completed memory report.
 * @returns Category-level byte sums including tracked grand total.
 */
export function buildAppMemoryCategoryTotals( report: AppMemoryReport ): AppMemoryCategoryTotals {
    return buildCategoryTotalsFromParts( {
        "ephemeralSlices":      report.ephemeralSlices,
        "persistedSlices":      report.persistedSlices,
        "drawnItinerary":       report.drawnItinerary,
        "mapStops":             report.mapStops,
        "itineraryMap":         report.itineraryMap,
        "activeItineraryBytes": report.activeItineraryBytes,
        "mapLibreTrackedBytes": report.mapLibreHints.mapLibreTrackedBytes,
        "realtime":             report.realtime,
        "vehicleLayer":         report.vehicleLayer,
        "tripBuffers":          report.tripBuffers,
        "indexedDbAxiosCache":  report.indexedDbAxiosCache,
        "heap":                 report.heap,
    } );
}

/**
 * Measures drawn itinerary geometry held in the ephemeral slice.
 *
 * @param geometry Active drawn geometry, if any.
 * @returns Coordinate counts and byte estimate.
 */
export function measureDrawnItineraryGeometry( geometry: DrawnItineraryGeometry | null ): SliceMemoryMetric & {
    "polylineCount":   number;
    "coordinateCount": number;
} {
    if ( geometry === null ) {
        return {
            "polylineCount":   0,
            "coordinateCount": 0,
            "estimatedBytes":  0,
        };
    }

    let coordinateCount = 0;
    for ( const polyline of geometry.polylines ) {
        coordinateCount += polyline.coordinates.length;
    }

    return {
        "polylineCount":  geometry.polylines.length,
        coordinateCount,
        "estimatedBytes": estimateJsonUtf8Bytes( geometry ),
        "detail":         `${ geometry.polylines.length } polylines, ${ coordinateCount } coordinates`,
    };
}

/**
 * Builds per-slice byte estimates for an in-memory store.
 *
 * @param slices Registered slice descriptors.
 * @param peek Returns the current in-memory value for a slice key.
 * @returns Metrics keyed by slice key.
 */
function measureSliceRegistry(
    slices: readonly { "key": string }[],
    peek: ( slice: { "key": string } ) => unknown,
    options: {
        "skipJsonSerialization"?: boolean;
        "cached"?:                Record<string, SliceMemoryMetric>;
    } = {},
): Record<string, SliceMemoryMetric> {
    if ( options.skipJsonSerialization === true && options.cached !== undefined ) {
        return options.cached;
    }

    const metrics: Record<string, SliceMemoryMetric> = {};

    for ( const slice of slices ) {
        const value = peek( slice );
        const estimatedBytes = estimateJsonUtf8Bytes( value );
        let detail: string | undefined;

        if ( slice.key === "routeHistory" && Array.isArray( value ) ) {
            detail = `${ value.length } entries`;
        }
        if ( slice.key === "searchHistory" && Array.isArray( value ) ) {
            detail = `${ value.length } items`;
        }

        metrics[slice.key] = {
            estimatedBytes,
            detail,
        };
    }

    return metrics;
}

/**
 * Collects WebGL vehicle layer diagnostics when a layer is bound.
 *
 * @returns Layer metrics, or null when the layer is not registered.
 */
export function measureVehicleLayerMemory(): VehicleLayerMemoryDiagnostics | null {
    if ( boundVehicleLayer === null ) {
        return null;
    }

    return boundVehicleLayer.getMemoryDiagnostics();
}

/**
 * Collects trip recording and replay buffer sizes.
 *
 * @returns Combined buffer diagnostics.
 */
export function measureTripBuffersMemory(): TripBuffersMemoryDiagnostics {
    const recording = tripRecordingService.getMemoryDiagnostics();
    const replay = tripReplayService.getMemoryDiagnostics();

    return {
        "recordingState":       recording.state,
        "gpsBufferSamples":     recording.gpsBufferSamples,
        "compassBufferSamples": recording.compassBufferSamples,
        "estimatedBytes":       recording.estimatedBytes,
        "replayState":          replay.state,
        "replayTripLoaded":     replay.tripLoaded,
        "replayGpsSamples":     replay.gpsSampleCount,
        "replayCompassSamples": replay.compassSampleCount,
        "replayEstimatedBytes": replay.estimatedBytes,
    };
}

/**
 * Measures localforage keys used by the production axios persistent cache.
 *
 * @returns Key count and serialized byte estimate.
 */
export async function measureIndexedDbAxiosCacheMemory(): Promise<IndexedDbCacheMemoryMetrics> {
    const keys = await localforage.keys();
    let estimatedBytes = 0;

    for ( const key of keys ) {
        const item: unknown = await localforage.getItem( key );
        estimatedBytes += estimateJsonUtf8Bytes( item );
    }

    return {
        "keyCount": keys.length,
        estimatedBytes,
    };
}

/**
 * Builds a cross-cutting application memory report.
 *
 * @param params Report inputs and optional realtime handles.
 * @returns Structured memory breakdown.
 */
export function buildAppMemoryReport( params: {
    "context":   string;
    "depth"?:    MemoryScanDepth;
    "realtime"?: {
        "cache":                RealtimeCache;
        "vehicles"?:            Map<string, Vehicle>;
        "colorCache"?:          Map<string, Color>;
        "typeCache"?:           Map<string, VehicleType>;
        "interpolationEngine"?: InterpolationEngine;
    };
    "activeItinerary"?:     unknown;
    "indexedDbAxiosCache"?: IndexedDbCacheMemoryMetrics | null;
} ): AppMemoryReport {
    const depth = params.depth ?? "full";
    const includeSerialization = depth === "full";
    const reuseCachedSlices = depth !== "full" && isFullScanCacheFresh() && cachedFullScanSlices !== null;

    const ephemeralSlices = measureSliceRegistry(
        ALL_EPHEMERAL_SLICES,
        ( slice ) => ephemeralStore.peek( slice as never ),
        {
            "skipJsonSerialization": !includeSerialization,
            "cached":                cachedFullScanSlices?.ephemeralSlices,
        },
    );

    const persistedSlices = measureSliceRegistry(
        ALL_PERSISTED_SLICES,
        ( slice ) => persistentSliceStore.peek( slice as never ),
        {
            "skipJsonSerialization": !includeSerialization,
            "cached":                cachedFullScanSlices?.persistedSlices,
        },
    );

    const drawnGeometryValue = ephemeralStore.peek( drawnItineraryGeometrySlice );
    const drawnItinerary = reuseCachedSlices && cachedFullScanSlices !== null
        ? cachedFullScanSlices.drawnItinerary
        : measureDrawnItineraryGeometry( drawnGeometryValue );

    const resolvedRealtime = resolveRealtimeParams( params.realtime );
    const realtime = resolvedRealtime !== undefined
        ? buildRealtimeMemoryReport( {
            "context":             params.context,
            "cache":               resolvedRealtime.cache,
            "vehicles":            resolvedRealtime.vehicles,
            "colorCache":          resolvedRealtime.colorCache,
            "typeCache":           resolvedRealtime.typeCache,
            "interpolationEngine": resolvedRealtime.interpolationEngine,
        } )
        : null;

    const vehicleLayer = measureVehicleLayerMemory();
    const tripBuffers = measureTripBuffersMemory();

    const activeItinerarySource = params.activeItinerary !== undefined
        ? params.activeItinerary
        : lastActiveItineraryForProfiling;
    const activeItineraryBytes = reuseCachedSlices && cachedFullScanSlices !== null
        ? cachedFullScanSlices.activeItineraryBytes
        : activeItinerarySource !== null && activeItinerarySource !== undefined
            ? estimateJsonUtf8Bytes( activeItinerarySource )
            : 0;

    const indexedDbAxiosCache = params.indexedDbAxiosCache ?? lastIndexedDbAxiosCache;
    const mapLibreHints = measureMapLibreMemoryHints( { includeSerialization } );
    const heap = readJsHeapMetrics();

    const categoryTotals = buildCategoryTotalsFromParts( {
        ephemeralSlices,
        persistedSlices,
        drawnItinerary,
        "mapStops":             lastMapStopsStats,
        "itineraryMap":         lastItineraryMapStats,
        activeItineraryBytes,
        "mapLibreTrackedBytes": mapLibreHints.mapLibreTrackedBytes,
        realtime,
        vehicleLayer,
        tripBuffers,
        indexedDbAxiosCache,
        heap,
    } );

    if ( depth === "full" ) {
        cachedFullScanSlices = {
            ephemeralSlices,
            persistedSlices,
            drawnItinerary,
            activeItineraryBytes,
        };
        lastFullScanTimestampMs = Date.now();
    }

    return {
        "context":             params.context,
        "estimatedTotalBytes": categoryTotals.trackedTotalBytes,
        categoryTotals,
        heap,
        ephemeralSlices,
        persistedSlices,
        drawnItinerary,
        "mapStops":            lastMapStopsStats,
        "itineraryMap":        lastItineraryMapStats,
        vehicleLayer,
        tripBuffers,
        indexedDbAxiosCache,
        realtime,
        activeItineraryBytes,
        mapLibreHints,
    };
}

/**
 * Resolves formatter depth from scan context and heap movement.
 *
 * @param params Context, throttle, and scan depth inputs.
 * @returns light, standard, or full log depth.
 */
function resolveFormatterDepth( params: {
    "context":        string;
    "skipThrottle"?:  boolean;
    "throttledLight": boolean;
    "scanDepth":      MemoryScanDepth;
    "previousTotals": AppMemoryCategoryTotals | null;
    "currentTotals":  AppMemoryCategoryTotals;
} ): MemoryLogDepth {
    if ( params.skipThrottle === true || params.context === "manual" ) {
        return params.scanDepth === "light"
            ? "standard"
            : resolveMemoryLogDepth( {
                "context":        params.context,
                "skipThrottle":   true,
                "throttledLight": false,
                "forceFull":      true,
            } );
    }

    if ( params.scanDepth === "light" || params.throttledLight ) {
        return "light";
    }

    let depth = resolveMemoryLogDepth( {
        "context":        params.context,
        "skipThrottle":   params.skipThrottle,
        "throttledLight": false,
    } );

    const heapDelta = computeHeapUsedDelta( params.currentTotals, params.previousTotals );
    if ( params.context === "periodic" && depth === "full" &&
      ( heapDelta === undefined || Math.abs( heapDelta ) <= 2 * 1024 * 1024 ) ) {
        depth = "standard";
    }

    return depth;
}

/**
 * Logs a multi-subsystem memory breakdown when profiling is enabled.
 *
 * @param params Report inputs; pass `skipThrottle` for one-off dumps (e.g. init).
 * @returns Nothing.
 */
export function logAppMemoryReport( params: {
    "context":       string;
    "skipThrottle"?: boolean;
    "realtime"?: {
        "cache":                RealtimeCache;
        "vehicles"?:            Map<string, Vehicle>;
        "colorCache"?:          Map<string, Color>;
        "typeCache"?:           Map<string, VehicleType>;
        "interpolationEngine"?: InterpolationEngine;
    };
    "activeItinerary"?:  unknown;
    "includeIndexedDb"?: boolean;
} ): void {
    if ( !APP_DIAGNOSTICS.memoryProfilingEnabled ) {
        return;
    }

    const now = Date.now();
    const throttled = params.skipThrottle !== true &&
      now - lastLogTimestampMs < APP_DIAGNOSTICS.memoryProfilingMinIntervalMs;

    // evict-stale adds no signal when throttled (init/periodic already cover trends).
    if ( throttled && params.context === "evict-stale" ) {
        return;
    }

    const runLog = ( indexedDbAxiosCache: IndexedDbCacheMemoryMetrics | null ): void => {
        const now = Date.now();
        const throttledLight = params.skipThrottle !== true &&
          now - lastLogTimestampMs < APP_DIAGNOSTICS.memoryProfilingMinIntervalMs;
        const previousTotals = lastReport?.categoryTotals ?? null;

        let scanDepth = resolveScanDepth( params, now );
        if ( scanDepth === "light" && buildLightReport( params.context ) === null ) {
            scanDepth = "standard";
        }

        if ( params.context === "map-idle" && scanDepth !== "light" ) {
            const probeTotals = buildLightReport( params.context )?.categoryTotals ?? previousTotals;
            const heapDelta = computeHeapUsedDelta(
                probeTotals ?? lastReport?.categoryTotals ?? {
                    "trackedTotalBytes":    0,
                    "mapLibreTrackedBytes": 0,
                    "ephemeralStoreBytes":  0,
                    "persistedStoreBytes":  0,
                    "mapStopsBytes":        0,
                    "mapOverlayBytes":      0,
                    "realtimeBytes":        0,
                    "vehicleLayerBytes":    0,
                    "tripBuffersBytes":     0,
                    "indexedDbCacheBytes":  0,
                },
                previousTotals,
            );
            scanDepth = heapDelta !== undefined &&
              Math.abs( heapDelta ) > APP_DIAGNOSTICS.memoryIdleFullScanHeapDeltaBytes
                ? "full"
                : "standard";
        }

        const scanStartMs = performance.now();
        let report: AppMemoryReport;

        if ( scanDepth === "light" ) {
            report = buildLightReport( params.context ) ?? buildAppMemoryReport( {
                "context":         params.context,
                "depth":           "standard",
                "realtime":        params.realtime,
                "activeItinerary": params.activeItinerary,
                indexedDbAxiosCache,
            } );
        } else {
            report = buildAppMemoryReport( {
                "context":         params.context,
                "depth":           scanDepth,
                "realtime":        params.realtime,
                "activeItinerary": params.activeItinerary,
                indexedDbAxiosCache,
            } );
        }

        const scanElapsedMs = performance.now() - scanStartMs;
        if ( scanDepth === "full" && scanElapsedMs > 50 ) {
            memoryDiagnosticsWarn( `${ MEMORY_DIAGNOSTICS_LOG_PREFIX } full scan took ${ scanElapsedMs.toFixed( 1 ) } ms (> 50 ms budget)` );
        }

        if ( indexedDbAxiosCache !== null ) {
            lastIndexedDbAxiosCache = indexedDbAxiosCache;
        }

        const formatterDepth = resolveFormatterDepth( {
            "context":       params.context,
            "skipThrottle":  params.skipThrottle,
            throttledLight,
            "scanDepth":     scanDepth,
            previousTotals,
            "currentTotals": report.categoryTotals,
        } );

        logFormattedMemoryReport( {
            report,
            "triggerContext":  params.context,
            "depth":           formatterDepth,
            previousTotals,
            "throttledDetail": throttledLight,
        } );

        if ( !throttledLight ) {
            lastLogTimestampMs = now;
        }

        lastReport = report;
    };

    const shouldDefer = APP_DIAGNOSTICS.memoryDeferFullScan &&
      ( params.context === "periodic" || params.context === "map-idle" ) &&
      params.skipThrottle !== true;

    const execute = (): void => {
        if ( params.includeIndexedDb === true ) {
            void measureIndexedDbAxiosCacheMemory().
                then( ( metrics ) => {
                    runLog( metrics );
                } ).
                catch( ( error: unknown ) => {
                    memoryDiagnosticsWarn( `${ MEMORY_DIAGNOSTICS_LOG_PREFIX } Failed to measure localforage cache`, error );
                    runLog( null );
                } );

            return;
        }

        runLog( null );
    };

    if ( shouldDefer ) {
        scheduleDeferredMemoryLog( execute );

        return;
    }

    execute();
}

/**
 * Logs realtime plus other tracked subsystems (compat wrapper for realtime call sites).
 *
 * @param params Realtime cache and optional vehicle manager handles.
 * @returns Nothing.
 */
export function logRealtimeMemoryReport( params: {
    "context":              string;
    "cache":                RealtimeCache;
    "vehicles"?:            Map<string, Vehicle>;
    "colorCache"?:          Map<string, Color>;
    "typeCache"?:           Map<string, VehicleType>;
    "interpolationEngine"?: InterpolationEngine;
    "activeItinerary"?:     unknown;
} ): void {
    logAppMemoryReport( {
        "context":      params.context,
        "skipThrottle": params.context === "init" ||
          params.context === "persistence-ready" ||
          params.context === "services-init",
        "realtime": {
            "cache":               params.cache,
            "vehicles":            params.vehicles,
            "colorCache":          params.colorCache,
            "typeCache":           params.typeCache,
            "interpolationEngine": params.interpolationEngine,
        },
        "activeItinerary": params.activeItinerary,
    } );
}

export function buildGlobalAppMemoryReport( context: string ): AppMemoryReport {
    return buildAppMemoryReport( {
        context,
        "depth": "full",
    } );
}

/**
 * Registers dev-only globals for manual memory dumps.
 *
 * @param getReport Optional factory; defaults to {@link buildGlobalAppMemoryReport}.
 * @returns Nothing.
 */
export function registerAppMemoryDevHelper( getReport: () => AppMemoryReport = () => buildGlobalAppMemoryReport( "manual" ) ): void {
    if ( !import.meta.env.DEV ) {
        return;
    }

    const globalWindow = window as Window & {
        "__flowrideAppMemory"?:      ( includeIndexedDb?: boolean ) => AppMemoryReport | Promise<AppMemoryReport>;
        "__flowrideRealtimeMemory"?: () => AppMemoryReport;
    };

    globalWindow.__flowrideAppMemory = ( includeIndexedDb = true ) => {
        logAppMemoryReport( {
            "context":      "manual",
            "skipThrottle": true,
            includeIndexedDb,
        } );

        if ( includeIndexedDb ) {
            return measureIndexedDbAxiosCacheMemory().
                then( ( indexedDbAxiosCache ) => {
                    lastIndexedDbAxiosCache = indexedDbAxiosCache;

                    return buildAppMemoryReport( {
                        "context": "manual",
                        "depth":   "full",
                        indexedDbAxiosCache,
                    } );
                } );
        }

        return getReport();
    };

    globalWindow.__flowrideRealtimeMemory = () => {
        logAppMemoryReport( {
            "context":      "manual",
            "skipThrottle": true,
        } );

        return getReport();
    };
}
